import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Plus, Trash2, Pencil, Calendar as CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { todayISO } from "@/lib/dateFormat";

export default function TeacherJournal() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState(params.get("group") || "");
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ date: todayISO(), topic: "", content: "" });

  const openEdit = (e) => {
    setEditingId(e.id);
    setForm({ date: e.date, topic: e.topic, content: e.content });
    setOpen(true);
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ date: todayISO(), topic: "", content: "" });
    setOpen(true);
  };

  useEffect(() => {
    api.get("/groups").then((r) => {
      setGroups(r.data);
      if (!groupId && r.data.length) setGroupId(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!groupId) return;
    setParams({ group: groupId });
    api.get("/journal", { params: { group_id: groupId } }).then((r) => setEntries(r.data));
  }, [groupId]);

  const submit = async () => {
    if (!form.topic || !form.content) { toast.error(t("error_generic")); return; }
    try {
      if (editingId) {
        await api.patch(`/journal/${editingId}`, form);
      } else {
        await api.post("/journal", { ...form, group_id: groupId });
      }
      toast.success(t("saved"));
      setOpen(false);
      setEditingId(null);
      setForm({ date: todayISO(), topic: "", content: "" });
      const r = await api.get("/journal", { params: { group_id: groupId } });
      setEntries(r.data);
    } catch { toast.error(t("error_generic")); }
  };

  const remove = async (id) => {
    if (!confirm(t("confirm_delete"))) return;
    await api.delete(`/journal/${id}`);
    const r = await api.get("/journal", { params: { group_id: groupId } });
    setEntries(r.data);
  };

  return (
    <div>
      <PageHeader
        title={t("journal")}
        testId="journal-header"
        action={<Button data-testid="new-journal-button" onClick={openNew} disabled={!groupId}><Plus className="h-4 w-4" /> {t("new_journal_entry")}</Button>}
      />
      <div className="mb-5 max-w-md">
        <Label>{t("group")}</Label>
        <Select value={groupId} onValueChange={setGroupId}>
          <SelectTrigger data-testid="journal-group-select"><SelectValue placeholder={t("select_group")} /></SelectTrigger>
          <SelectContent>
            {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {entries.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-500">
            {t("no_data")}
          </div>
        )}
        {entries.map((e) => (
          <Card key={e.id} className="p-5" data-testid={`journal-entry-${e.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                  <CalendarIcon className="h-3.5 w-3.5" /> {e.date}
                </div>
                <h3 className="font-display text-lg font-semibold text-slate-900">{e.topic}</h3>
                <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{e.content}</p>
              </div>
              <div className="flex items-center gap-0.5">
                <Button variant="ghost" size="icon" onClick={() => openEdit(e)} className="text-slate-500" data-testid={`edit-journal-${e.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => remove(e.id)} className="text-red-600" data-testid={`delete-journal-${e.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingId ? t("edit") : t("new_journal_entry")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("date")}</Label>
              <Input type="date" data-testid="journal-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <Label>{t("topic")}</Label>
              <Input data-testid="journal-topic-input" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} />
            </div>
            <div>
              <Label>{t("content")}</Label>
              <Textarea rows={6} data-testid="journal-content-input" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="save-journal-button" onClick={submit}>{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
